import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Copy, Check, Info, Loader2 } from 'lucide-react';
import {
  useDocumentTemplates,
  useDocumentTemplate,
} from '@/hooks/react-query/useTemplates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import {
  TemplateCategory,
  AVAILABLE_VARIABLES,
} from '@/components/admin/templates/constants';

export default function DocumentTemplateFormPage() {
  const { templateId } = useParams<{ templateId?: string }>();

  if (!templateId) {
    return <DocumentTemplateForm key="new" />;
  }

  return <DocumentTemplateFormPageEdit templateId={templateId} />;
}

function DocumentTemplateFormPageEdit({ templateId }: { templateId: string }) {
  const { data: template, isLoading } = useDocumentTemplate(templateId);

  if (isLoading) {
    return (
      <div className="container mx-auto space-y-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-64" />
          <div className="h-4 bg-muted rounded w-96" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  return (
    <DocumentTemplateForm
      key={templateId}
      initialData={
        template
          ? {
              name: template.name,
              description: template.description || '',
              category: template.template_type as TemplateCategory,
              html_content: template.html_content || '',
            }
          : undefined
      }
      templateId={templateId}
    />
  );
}

interface DocumentTemplateFormProps {
  initialData?: {
    name: string;
    description: string;
    category: TemplateCategory | '';
    html_content: string;
  };
  templateId?: string;
}

function DocumentTemplateForm({
  initialData,
  templateId,
}: DocumentTemplateFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!templateId;

  const {
    createHtmlDocumentTemplate,
    updateDocumentTemplate,
    isCreatingHtmlDocument,
    isUpdatingDocument,
  } = useDocumentTemplates();

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    category: (initialData?.category || '') as TemplateCategory | '',
    html_content: initialData?.html_content || '',
  });
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleCopyVariable = (variable: string) => {
    navigator.clipboard
      .writeText(`{{ ${variable} }}`)
      .then(() => {
        setCopiedVariable(variable);
        setTimeout(() => setCopiedVariable(null), 1500);
      })
      .catch(() =>
        toast({
          title: 'Could not copy to clipboard',
          variant: 'destructive',
        })
      );
  };

  const handleDragStart = (e: React.DragEvent, variable: string) => {
    e.dataTransfer.setData('text/plain', `{{ ${variable} }}`);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const text = e.dataTransfer.getData('text/plain');
    if (!text) return;

    const textarea = textareaRef.current;
    if (!textarea) return;

    const pos = textarea.selectionStart ?? formData.html_content.length;
    const before = formData.html_content.substring(0, pos);
    const after = formData.html_content.substring(pos);
    setFormData((prev) => ({ ...prev, html_content: before + text + after }));

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = pos + text.length;
    });
  };

  const handleSave = async () => {
    if (!formData.name || !formData.category || !formData.html_content) {
      toast({
        title: 'Validation Error',
        description:
          'Please fill in all required fields (Name, Category, HTML Content)',
        variant: 'destructive',
      });
      return;
    }
    try {
      if (isEditing && templateId) {
        await updateDocumentTemplate({
          id: templateId,
          data: {
            name: formData.name,
            description: formData.description || undefined,
            html_content: formData.html_content,
          },
        });
        toast({
          title: 'Success',
          description: 'Template updated successfully',
        });
      } else {
        await createHtmlDocumentTemplate({
          name: formData.name,
          description: formData.description || undefined,
          template_type: formData.category,
          html_content: formData.html_content,
        });
        toast({
          title: 'Success',
          description: 'Template created successfully',
        });
      }
      navigate('/app/admin/templates');
    } catch {
      toast({
        title: 'Error',
        description: `Failed to ${isEditing ? 'update' : 'create'} template`,
        variant: 'destructive',
      });
    }
  };

  const isSaving = isCreatingHtmlDocument || isUpdatingDocument;

  return (
    <div className="space-y-6">
      {/* Description */}
      <p className="text-muted-foreground">
        Write a Jinja2 HTML template with merge fields (e.g.,{' '}
        {`{{ investor.name }}`}). This template will be rendered to PDF during
        notice generation.
      </p>

      {/* Form Fields */}
      <Card>
        <CardHeader>
          <CardTitle>Template Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="doc-name">Template Name *</Label>
              <Input
                id="doc-name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Distribution Notice - Simplified"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="doc-category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    category: value as TemplateCategory,
                  })
                }
                disabled={isEditing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="distribution">
                    Distribution Notice
                  </SelectItem>
                  <SelectItem value="drawdown">Drawdown Notice</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="doc-description">Description</Label>
            <Input
              id="doc-description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Brief description of this template variant"
            />
          </div>
        </CardContent>
      </Card>

      {/* Available Variables */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Available Variables</span>
            <span className="text-xs text-muted-foreground">
              click to copy · drag into template
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {AVAILABLE_VARIABLES.map((v) => (
              <button
                key={v}
                type="button"
                draggable
                onClick={() => handleCopyVariable(v)}
                onDragStart={(e) => handleDragStart(e, v)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono bg-background border hover:bg-accent transition-colors cursor-grab active:cursor-grabbing"
              >
                {copiedVariable === v ? (
                  <Check className="h-3 w-3 text-green-500" />
                ) : (
                  <Copy className="h-3 w-3 text-muted-foreground" />
                )}
                {`{{ ${v} }}`}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* HTML Content */}
      <Card>
        <CardHeader>
          <CardTitle>HTML Template *</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            ref={textareaRef}
            id="doc-html"
            value={formData.html_content}
            onChange={(e) =>
              setFormData({ ...formData, html_content: e.target.value })
            }
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={handleDrop}
            placeholder={`<!DOCTYPE html>\n<html lang="en">\n<head>\n  <title>Distribution Notice - {{ fund.name }}</title>\n</head>\n<body>\n  <h1>Distribution Notice</h1>\n  <p>Dear {{ investor.name }},</p>\n  ...\n</body>\n</html>`}
            rows={20}
            className="font-mono text-sm leading-relaxed"
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/app/admin/templates')}
        >
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {isEditing ? 'Update' : 'Create'} Template
        </Button>
      </div>
    </div>
  );
}
